import { z } from 'zod';

import { BooleanParamSchema } from '../../common/boolean-param/boolean-param';
import { makeListQuery } from '../../common/list-query/list-query';
import {
  BILLING_EVENT_APP_USER_ID_MAX_LENGTH,
  BILLING_EVENT_EXTERNAL_ID_MAX_LENGTH,
  BILLING_EVENT_TYPE_MAX_LENGTH,
} from '../billing-event/billing-event';

/**
 * The billing trail's filters (N7).
 *
 * Four, and each answers a question somebody actually asks at a desk:
 * `appUserId` — "what has this account's billing been doing?" (the composite
 * index exists for exactly it); `eventType` — "show me the refunds";
 * `externalEventId` — "RevenueCat says it sent us this, did we get it?";
 * `processed` — "what is stuck right now?".
 *
 * ## `processed` is a real boolean, unlike the orders queue's `needsEntitlement`
 *
 * P23b2's rule is conditional: a flag rather than a boolean **if** the negative
 * is the unfiltered list. Here it is not. `processed=true` ("what landed") and
 * `processed=false` ("what is stuck") are two genuine halves an operator moves
 * between, so `BooleanParamSchema` is the honest control and `z.literal('true')`
 * would deny half the surface's purpose.
 *
 * It still cannot be a plain column filter behind the scenes: `op: 'isNull'`
 * **discards its value**, so `false` would have narrowed exactly as `true` did.
 * The spec entry is an `exists` build with both branches written out — the same
 * trap `DIRECT_PAYMENT_ORDER_FILTER_SPEC` records, reached for a different
 * reason.
 *
 * ## `appUserId` and `eventType` are free strings, and both are on purpose
 *
 * `appUserId` is not `UuidSchema` for the reason `BillingEventSchema` gives on
 * that field: the column has no foreign key, and the malformed id is the row
 * worth finding. `eventType` is not
 * `RevenueCatEventTypeSchema` because `RevenueCatEventSchema.type` is itself a
 * plain string — an unrecognised type reaches the mapper and is recorded rather
 * than refused (P23a2), and a filter that could not name it would hide the one
 * row that matters on the day RevenueCat ships a new one.
 *
 * ## There is no `search`
 *
 * Nothing here is prose. The ids are exact matches because that is how they
 * arrive — pasted from the vendor's dashboard — and a `search` over the `error`
 * column would promise a text index it does not have. A filter this list does not
 * offer is one an operator cannot be misled by.
 */
export const BillingEventsListQuerySchema = makeListQuery({
  appUserId: z.string().trim().min(1).max(BILLING_EVENT_APP_USER_ID_MAX_LENGTH).optional(),
  eventType: z.string().trim().min(1).max(BILLING_EVENT_TYPE_MAX_LENGTH).optional(),
  externalEventId: z.string().trim().min(1).max(BILLING_EVENT_EXTERNAL_ID_MAX_LENGTH).optional(),
  processed: BooleanParamSchema.optional(),
});

export type BillingEventsListQuery = z.infer<typeof BillingEventsListQuerySchema>;
